/**
 * Admin — compose + manage app-wide announcements. Posting a new one makes it
 * the single active announcement (retiring any previous), shown to all users
 * in the top-of-app banner.
 */

import { useState } from 'react'
import { ErrorCard } from '../components/ErrorCard'
import {
  makeStyles,
  tokens,
  Title2,
  Subtitle2,
  Body1,
  Caption1,
  Button,
  Input,
  Textarea,
  Select,
  Badge,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components'
import {
  useAnnouncements,
  useCreateAnnouncement,
  useDeactivateAnnouncement,
} from '../hooks/useAnnouncements'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import type { AnnouncementSeverity } from '../types/announcement'

const SEVERITIES: { key: AnnouncementSeverity; label: string; badge: 'informative' | 'warning' | 'danger' }[] = [
  { key: 'info', label: 'Info (blue)', badge: 'informative' },
  { key: 'warning', label: 'Warning (amber)', badge: 'warning' },
  { key: 'critical', label: 'Critical (red)', badge: 'danger' },
]

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxWidth: '760px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  field: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalM,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  rowBody: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
})

export function AdminAnnouncements() {
  const styles = useStyles()
  const { showToast } = useToast()
  const { data, isLoading, error, refetch } = useAnnouncements()
  const create = useCreateAnnouncement()
  const deactivate = useDeactivateAnnouncement()

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [severity, setSeverity] = useState<AnnouncementSeverity>('info')

  const canPost = title.trim().length >= 3 && body.trim().length >= 3

  const onPost = async () => {
    try {
      await create.mutateAsync({ title: title.trim(), body: body.trim(), severity })
      setTitle('')
      setBody('')
      setSeverity('info')
      showToast({ title: 'Announcement published', intent: 'success' })
    } catch (err) {
      showToast({ title: 'Could not publish', body: apiErrorMessage(err), intent: 'error' })
    }
  }

  const onRetire = async (id: number) => {
    try {
      await deactivate.mutateAsync(id)
      showToast({ title: 'Announcement retired', intent: 'success' })
    } catch (err) {
      showToast({ title: 'Could not retire', body: apiErrorMessage(err), intent: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <header>
        <Title2 as="h1">Announcements</Title2>
        <Caption1>
          Shown to everyone signed in. A new one replaces the current one.
        </Caption1>
      </header>

      <section className={styles.card}>
        <Subtitle2>New announcement</Subtitle2>
        <div className={styles.field}>
          <Caption1>Title</Caption1>
          <Input
            value={title}
            onChange={(_, d) => setTitle(d.value)}
            placeholder="DACcelerate is now on v2"
            maxLength={200}
          />
        </div>
        <div className={styles.field}>
          <Caption1>Message</Caption1>
          <Textarea
            value={body}
            onChange={(_, d) => setBody(d.value)}
            placeholder="What changed, and where to go with questions…"
            rows={5}
            resize="vertical"
          />
        </div>
        <div className={styles.field}>
          <Caption1>Severity (banner color)</Caption1>
          <Select
            value={severity}
            onChange={(_, d) => setSeverity(d.value as AnnouncementSeverity)}
            data-testid="announcement-severity"
          >
            {SEVERITIES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </Select>
        </div>
        <div className={styles.actions}>
          <Button appearance="primary" disabled={!canPost || create.isPending} onClick={onPost}>
            {create.isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </div>
      </section>

      <section>
        <Subtitle2>History</Subtitle2>
        {isLoading ? (
          <Spinner label="Loading…" />
        ) : error ? (
          <div data-testid="announcements-error">
            <ErrorCard
              title="Couldn't load announcements"
              message={apiErrorMessage(error, 'Please try again.')}
              retry={() => {
                void refetch()
              }}
            />
          </div>
        ) : !data || data.items.length === 0 ? (
          <MessageBar>
            <MessageBarBody>No announcements yet.</MessageBarBody>
          </MessageBar>
        ) : (
          <div className={styles.list}>
            {data.items.map((a) => (
              <div key={a.id} className={styles.row}>
                <div className={styles.rowBody}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong>{a.title}</strong>
                    <Badge
                      appearance="tint"
                      color={SEVERITIES.find((s) => s.key === a.severity)?.badge ?? 'informative'}
                      size="small"
                    >
                      {a.severity}
                    </Badge>
                    {a.is_active ? (
                      <Badge appearance="filled" color="success" size="small">
                        Active
                      </Badge>
                    ) : (
                      <Badge appearance="outline" color="subtle" size="small">
                        Retired
                      </Badge>
                    )}
                  </div>
                  <Body1 style={{ whiteSpace: 'pre-wrap' }}>{a.body}</Body1>
                  <Caption1>{new Date(a.created_at).toLocaleString()}</Caption1>
                </div>
                {a.is_active && (
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={deactivate.isPending}
                    onClick={() => onRetire(a.id)}
                  >
                    Retire
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export default AdminAnnouncements
